import { HelpFeature } from '@/model/HelpModel';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Button, CheckboxProps, Input, notification, Space, Spin, Table, TablePaginationConfig } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult, TableRowSelection } from 'antd/lib/table/interface';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { deviceAdminApi } from '../../../../../api';
import {
	buildColumn,
	getDefaultPaginationSettings,
	getDefaultTablePaginationConfig,
	getUniqueValuesArray,
	handleResponse,
	invertSelectedRowKeys,
	removeCommasAndPercentSign,
	ScrollType,
	useHandleOnChangeTableLogic,
} from '../../../../../Helper';
import { getPermissionErrorMessage, SecuredComponents, User } from '../../../../../model/AccountModel';
import { HolidayController, HolidayControllerColumn, HolidayControllerPaginationSetting, HolidayGroup } from '../../../../../model/DeviceAdminModel';
import { HelpFactoryComponent, Modal, SearchColumn } from '../../../../common';
import styles from './holidayGroupModal.module.scss';

type Props = {
	holidayGroup: HolidayGroup;
	onConfirm: () => void;
	onCancel: () => void;
};

const user: User = getUser();
const componentPermission = User.getComponentPermission(user, SecuredComponents.Holiday);
const topicIdHolidayGroup: string = 'AddHolidayGroup';

const defaultControllerPagination = (holidayGroupId: number, isSource: boolean): HolidayControllerPaginationSetting => {
	return {
		...getDefaultPaginationSettings(),
		HolidayGroupId: holidayGroupId,
		AddedKeys: [],
		RemovedKeys: [],
		IsSource: isSource,
	};
};

const scroll: ScrollType = { y: 200 };

const HolidayGroupModal: React.FC<Props> = ({ holidayGroup, onConfirm, onCancel }) => {
	//#region state management variables
	const [name, setName] = useState<string>(holidayGroup.Name);
	const [isNameDuplicate, setIsNameDuplicate] = useState<boolean>(false);
	const [isNameEmpty, setIsNameEmpty] = useState<boolean>(false);
	const [loadingModal, setLoadingModal] = useState<boolean>(false);
	const [loadingSource, setLoadingSource] = useState<boolean>(true);
	const [loadingTarget, setLoadingTarget] = useState<boolean>(true);
	const [controllersSource, setControllersSource] = useState<HolidayControllerColumn[]>([]);
	const [controllersTarget, setControllersTarget] = useState<HolidayControllerColumn[]>([]);
	const [paginationSettingsSource, setPaginationSettingsSource] = useState<HolidayControllerPaginationSetting>(
		defaultControllerPagination(holidayGroup.Id, true)
	);
	const [paginationSettingsTarget, setPaginationSettingsTarget] = useState<HolidayControllerPaginationSetting>(
		defaultControllerPagination(holidayGroup.Id, false)
	);
	const [selectedKeysSource, setSelectedKeysSource] = useState<React.Key[]>([]);
	const [selectedKeysTarget, setSelectedKeysTarget] = useState<React.Key[]>([]);
	const [cachedSource, setCachedSource] = useState<number[]>([]);
	const [cachedTarget, setCachedTarget] = useState<number[]>([]);
	const [resetSearchColumnSource, setResetSearchColumnSource] = useState<boolean>(false);
	const [resetSearchColumnTarget, setResetSearchColumnTarget] = useState<boolean>(false);
	const [tableScroll, setTableScroll] = useState<ScrollType>();
	const isSearchPerformedSource = useRef<boolean>(false);
	const isSearchPerformedTarget = useRef<boolean>(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLDivElement>(null);
	//#endregion
	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	useEffect(() => {
		loadTables(paginationSettingsSource, paginationSettingsTarget);

		const handleBeforeUnload = () => {
			deviceAdminApi.unlockComponentDeviceAdmin(holidayGroup.Id, SecuredComponents.HolidayGroup);
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			handleBeforeUnload();
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, []);

	useEffect(() => {
		const updateTableHeight = () => {
			if (containerRef.current && inputRef.current) {
				const containerHeight = containerRef.current.offsetHeight;
				const inputHeight = inputRef.current.offsetHeight;
				const height = Math.round(containerHeight - inputHeight - 125);
				const tableBodies = containerRef.current.querySelectorAll('.ant-table-body') as NodeListOf<HTMLElement>;
				if (tableBodies) {
					tableBodies.forEach((tableBody: HTMLElement) => {
						tableBody.style.height = `${height}px`;
					});
					const placeholders = containerRef.current.querySelectorAll('.ant-table-placeholder') as NodeListOf<HTMLElement>;
					if (placeholders) {
						placeholders.forEach((placeholder: HTMLElement) => {
							placeholder.style.height = `${height}px`;
						});
					}
					setTableScroll({ y: height });
				}
			}
		};

		updateTableHeight();

		const observer = new MutationObserver(() => {
			updateTableHeight();
			observer.disconnect();
		});

		if (containerRef.current) {
			observer.observe(containerRef.current, { childList: true, subtree: true });
		}

		window.addEventListener('resize', updateTableHeight);

		return () => {
			window.removeEventListener('resize', updateTableHeight);
			observer.disconnect();
		};
	}, [JSON.stringify(paginationSettingsSource), JSON.stringify(paginationSettingsTarget)]);

	const loadTables = (pageSettingSource: HolidayControllerPaginationSetting, pageSettingTarget: HolidayControllerPaginationSetting) => {
		setLoading(true, true);
		const sourceQuery = deviceAdminApi.getHolidayControllers(pageSettingSource);
		const targetQuery = deviceAdminApi.getHolidayControllers(pageSettingTarget);
		Promise.all([sourceQuery, targetQuery])
			.then(responses => {
				let isValid = !handleResponse(responses[0]);
				isValid = !handleResponse(responses[1]) && isValid;
				if (isValid) {
					const {
						Entity: { HolidayControllers: controllersSource, TotalItemsPaginated: totalItemsSource },
					} = responses[0];
					const {
						Entity: { HolidayControllers: controllersTarget, TotalItemsPaginated: totalItemsTarget },
					} = responses[1];
					batch(() => {
						setControllersSource(mapDataForPresentation(controllersSource));
						setControllersTarget(mapDataForPresentation(controllersTarget));
						setPaginationSettingsSource({ ...pageSettingSource, TotalItems: totalItemsSource });
						setPaginationSettingsTarget({ ...pageSettingTarget, TotalItems: totalItemsTarget });
					});
				}
			})
			.finally(() => {
				setLoading(false, false);
			});
	};

	const loadTable = (pageSettings: HolidayControllerPaginationSetting) => {
		const isSource = pageSettings.IsSource;
		setLoading(isSource, !isSource);
		deviceAdminApi
			.getHolidayControllers(pageSettings)
			.then(response => {
				if (!handleResponse(response)) {
					const {
						Entity: { HolidayControllers, TotalItemsPaginated },
					} = response;

					batch(() => {
						if (isSource) {
							setControllersSource(mapDataForPresentation(HolidayControllers));
							setPaginationSettingsSource({ ...pageSettings, TotalItems: TotalItemsPaginated });
						} else {
							setControllersTarget(mapDataForPresentation(HolidayControllers));
							setPaginationSettingsTarget({ ...pageSettings, TotalItems: TotalItemsPaginated });
						}
					});
				}
			})
			.finally(() => {
				setLoading(false, false);
			});
	};

	const setLoading = (loadSource: boolean, loadTarget: boolean) => {
		batch(() => {
			setLoadingSource(loadSource);
			setLoadingTarget(loadTarget);
		});
	};

	const mapDataForPresentation = (data: HolidayController[]): HolidayControllerColumn[] => {
		if (data) {
			return data.map(d => ({
				key: `${d.Id}`,
				Id: d.Id,
				Name: d.Name,
				Address: d.Address,
				HolidayGroupName: d.HolidayGroup.Name,
				Action: undefined,
			}));
		}
		return [];
	};

	const getColumns = (isSource: boolean) => {
		const columnKeyPrefix = isSource ? 'source' : 'target';
		let columns: ColumnsType<HolidayControllerColumn> = [
			{
				...buildColumn(_('ControllerName'), 'Name', '200px', 'start'),
				key: `${columnKeyPrefix}ColumnName`,
				editable: false,
				sorter: true,
				...SearchColumn({
					maxLength: 20,
					dataIndex: 'Name',
					resetSearch: isSource ? resetSearchColumnSource : resetSearchColumnTarget,
					clearSelection: () => clearSelection(isSource),
					handleResetSearch: isSource ? () => setResetSearchColumnSource(false) : () => setResetSearchColumnTarget(false),
					setSearchResults: (searchedValue: string) => handleOnSearch(searchedValue, isSource),
					setSearchPerformed: (value: boolean) => setSearchPerformedSource(value, isSource),
				}),
			},
			{
				...buildColumn(_('Address'), _('Address'), 'auto', 'start'),
				key: `${columnKeyPrefix}ColumnAddress`,
				editable: false,
				sorter: true,
			},
		];

		if (isSource) {
			columns.push({
				...buildColumn(_('CurrentGroup'), 'HolidayGroupName', 'auto', 'start'),
				key: `${columnKeyPrefix}ColumnHolidayGroupName`,
				editable: false,
				sorter: true,
			});
		}

		return columns;
	};

	const handleOnSearch = (searchString: string, isSource: boolean) => {
		const pageSettings: HolidayControllerPaginationSetting = isSource
			? {
					...paginationSettingsSource,
					PageNumber: 1,
					SearchedValue: searchString,
			  }
			: {
					...paginationSettingsTarget,
					PageNumber: 1,
					SearchedValue: searchString,
			  };
		loadTable(pageSettings);
	};

	const setSearchPerformedSource = (value: boolean, isSource: boolean) => {
		if (isSource) {
			isSearchPerformedSource.current = value;
		} else {
			isSearchPerformedTarget.current = value;
		}
	};

	const clearSelection = (isSource: boolean) => {
		if (isSource) {
			setSelectedKeysSource([]);
		} else {
			setSelectedKeysTarget([]);
		}
	};

	const handleSelectAll = (isSource: boolean) => {
		setLoading(isSource, !isSource);
		deviceAdminApi
			.getHolidayControllerIds(holidayGroup.Id, isSource)
			.then(response => {
				if (!handleResponse(response)) {
					const returnedIds: string[] = response.Entity.map(id => id.toString());
					if (isSource) {
						setSelectedKeysSource(getUniqueValuesArray(selectedKeysSource, returnedIds));
					} else {
						setSelectedKeysTarget(getUniqueValuesArray(selectedKeysTarget, returnedIds));
					}
				}
			})
			.finally(() => {
				setLoading(false, false);
			});
	};

	const handleSelectInvertTarget = (isSource: boolean) => {
		if (isSource) {
			const sourceKeys = controllersSource.map<React.Key>(cs => cs.key);
			setSelectedKeysSource(invertSelectedRowKeys(sourceKeys, selectedKeysSource));
		} else {
			const targetKeys = controllersTarget.map<React.Key>(cs => cs.key);
			setSelectedKeysTarget(invertSelectedRowKeys(targetKeys, selectedKeysTarget));
		}
	};

	const getRowSelection = (isSource: boolean) => {
		const keyPrefix = isSource ? 'Source' : 'Target';
		const rowSelectionProps: TableRowSelection<HolidayControllerColumn> = {
			preserveSelectedRowKeys: true,
			type: 'checkbox',
			selections: [
				{ key: 'SELECTION_ALL_TARGET', text: 'Select all data', onSelect: () => handleSelectAll(isSource) },
				{ key: 'SELECTION_INVERT_TARGET', text: 'Invert current page', onSelect: () => handleSelectInvertTarget(isSource) },
				Table.SELECTION_NONE,
			],
			onChange: (selectedRowKeys: React.Key[], selectedRows: HolidayControllerColumn[]) => {
				if (isSource) {
					setSelectedKeysSource(selectedRowKeys);
				} else {
					setSelectedKeysTarget(selectedRowKeys);
				}
			},
			getCheckboxProps: record => {
				const disabled: boolean = false;
				const props: CheckboxProps = {
					id: `holidayGroup${keyPrefix}TableCheckbox-${record.key}`,
					disabled,
				};
				return props;
			},
			columnWidth: 60,
			selectedRowKeys: isSource ? selectedKeysSource : selectedKeysTarget,
		};
		return rowSelectionProps;
	};

	const onAddControllerToHolidayGroup = () => {
		const clonedCachedSource = [...cachedSource];
		const clonedCachedTarget = [...cachedTarget];

		for (const selectedController of selectedKeysSource) {
			const controllerIndex = clonedCachedSource.findIndex(x => x === Number(selectedController));
			if (~controllerIndex) {
				clonedCachedSource.splice(controllerIndex, 1);
			}

			const index = clonedCachedTarget.findIndex(x => x === Number(selectedController));
			if (index === -1) {
				clonedCachedTarget.push(Number(selectedController));
			}
		}

		setCachedSource(clonedCachedSource);
		setCachedTarget(clonedCachedTarget);
		setSelectedKeysSource([]);

		const pageSettingSource: HolidayControllerPaginationSetting = {
			...defaultControllerPagination(holidayGroup.Id, true),
			PageSize: paginationSettingsSource.PageSize,
			AddedKeys: clonedCachedSource,
			RemovedKeys: clonedCachedTarget,
		};

		const pageSettingTarget: HolidayControllerPaginationSetting = {
			...paginationSettingsTarget,
			AddedKeys: clonedCachedTarget,
			RemovedKeys: clonedCachedSource,
		};

		loadTables(pageSettingSource, pageSettingTarget);
	};

	const onRemoveControllerFromHolidayGroup = () => {
		const clonedCachedSource = [...cachedSource];
		const clonedCachedTarget = [...cachedTarget];

		for (const selectedController of selectedKeysTarget) {
			const controllerIndex = clonedCachedTarget.findIndex(x => x === Number(selectedController));
			if (~controllerIndex) {
				clonedCachedTarget.splice(controllerIndex, 1);
			}

			const index = clonedCachedSource.findIndex(x => x === Number(selectedController));
			if (index === -1) {
				clonedCachedSource.push(Number(selectedController));
			}
		}

		batch(() => {
			setCachedSource(clonedCachedSource);
			setCachedTarget(clonedCachedTarget);
			setSelectedKeysTarget([]);
		});

		const pageSettingSource: HolidayControllerPaginationSetting = {
			...paginationSettingsSource,
			AddedKeys: clonedCachedSource,
			RemovedKeys: clonedCachedTarget,
		};

		const pageSettingTarget: HolidayControllerPaginationSetting = {
			...defaultControllerPagination(holidayGroup.Id, false),
			PageSize: paginationSettingsTarget.PageSize,
			AddedKeys: clonedCachedTarget,
			RemovedKeys: clonedCachedSource,
		};

		loadTables(pageSettingSource, pageSettingTarget);
	};

	const handlePagination = (page: number, pageSize: number, isSource: boolean) => {
		if (isSource) {
			if (pageSize !== paginationSettingsSource.PageSize) {
				clearSelection(true);
			}
		} else {
			if (pageSize !== paginationSettingsTarget.PageSize) {
				clearSelection(false);
			}
		}
	};

	const handleOnChangeTable = (
		pagination: TablePaginationConfig,
		filters: { Name?: string[] },
		sorter: SorterResult<HolidayControllerColumn>,
		isSource: boolean
	) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection: () => clearSelection(isSource),
			filters,
			handleChangePagination: (page: number, pageSize: number) => handlePagination(page, pageSize, isSource),
			isSearchPerformed: isSource ? isSearchPerformedSource : isSearchPerformedTarget,
			pagination,
			shouldResetSearchColumn: false,
			sorter,
			tablePaginationSetting: isSource ? paginationSettingsSource : paginationSettingsTarget,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			const fromPageSettings = isSource ? paginationSettingsSource : paginationSettingsTarget;
			const pageSettings: HolidayControllerPaginationSetting = {
				...fromPageSettings,
				PageNumber: current,
				PageSize: pageSize,
				SortDirection: sortOrder,
				SortField: sortField.toString(),
			};
			loadTable(pageSettings);
		}
	};

	const getPagination = (isSourceTable: boolean): TablePaginationConfig => {
		if (isSourceTable) {
			return getDefaultTablePaginationConfig(
				false,
				(page: number, pageSize: number) => handlePagination(page, pageSize, true),
				paginationSettingsSource.PageNumber,
				paginationSettingsSource.PageSize,
				paginationSettingsSource.TotalItems,
				undefined,
				selectedKeysSource
			);
		} else {
			return getDefaultTablePaginationConfig(
				false,
				(page: number, pageSize: number) => handlePagination(page, pageSize, false),
				paginationSettingsTarget.PageNumber,
				paginationSettingsTarget.PageSize,
				paginationSettingsTarget.TotalItems,
				undefined,
				selectedKeysTarget
			);
		}
	};

	const handleSaveHolidayGroup = () => {
		const id = holidayGroup.Id;
		const holidayGroupToSave: HolidayGroup = {
			Id: id,
			Name: name.trim(),
		};

		setLoadingModal(true);
		deviceAdminApi
			.isHolidayGroupNameUnique(holidayGroupToSave)
			.then(uniqueResponse => {
				if (!handleResponse(uniqueResponse)) {
					const { Entity: isUnique } = uniqueResponse;
					if (isUnique) {
						return id > 0
							? deviceAdminApi.updateHolidayGroup(holidayGroupToSave, cachedTarget, cachedSource)
							: deviceAdminApi.addNewHolidayGroup(holidayGroupToSave, cachedTarget);
					} else {
						setIsNameDuplicate(true);
						notification.error({
							message: _(uniqueResponse.ErrorMessage),
						});
					}
				}
			})
			.then(response => {
				if (!handleResponse(response)) {
					onConfirm();
				}
			})
			.finally(() => {
				setLoadingModal(false);
			});
	};

	const handleOnSave = () => {
		if (name.trim() === '') {
			setIsNameEmpty(true);
			return;
		}
		handleSaveHolidayGroup();
	};

	const handleOnCancel = () => {
		onCancel();
	};

	const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value: string = removeCommasAndPercentSign(e.currentTarget.value);
		batch(() => {
			setIsNameEmpty(false);
			setIsNameDuplicate(false);
			setName(value);
		});
	};

	const permissionsEnabled = holidayGroup.Id > 0 ? componentPermission.canUpdate : componentPermission.canAdd;
	const disabled = !permissionsEnabled;
	const disableAddButton: boolean = selectedKeysSource.length === 0;
	const disableRemoveButton: boolean = selectedKeysTarget.length === 0;

	const footer = [
		<div className={styles.buttonsContainer}>
			<Space>
				<Button
					key='save'
					title={getPermissionErrorMessage(permissionsEnabled)}
					type='primary'
					id='holidayGroupAddSaveChanges'
					disabled={disabled}
					onClick={() => handleOnSave()}>
					{_('SaveChanges')}
				</Button>
				<Button key='cancel' onClick={() => handleOnCancel()} id='holidayGroupAddCancel'>
					{_('Cancel')}
				</Button>
			</Space>
		</div>,
	];

	return (
		<Modal
			addHelpButton={<HelpFactoryComponent helpFeature={HelpFeature.DigitracHolidays} topicId={topicIdHolidayGroup} size='medium' />}
			visible
			footer={footer}
			width={'1600px'}
			customZoomClass={styles.withModalZoom}
			title={holidayGroup.Id > 0 ? _('EditHolidayGroup') : _('AddHolidayGroup')}
			onCancel={handleOnCancel}>
			<Spin tip={`${_('Loading')}...`} spinning={loadingModal} size='large'>
				<div ref={containerRef} className={styles.container}>
					<div ref={inputRef} className={styles.inputContainer}>
						<label htmlFor={'holidayGroupModalInput'}>{_('Name')}</label>
						<Input
							id={'holidayGroupModalInput'}
							className={cx('', {
								[styles.error]: isNameDuplicate || isNameEmpty,
							})}
							maxLength={50}
							value={name}
							onChange={handleChangeName}
						/>
					</div>
					{isNameEmpty && <p className={styles.errorMessage}>{_('FilterGroupErrorMessage')}</p>}
					<div className={styles.sideBySideContainer}>
						<div className={styles.leftContainer}>
							<label className={styles.labelStyle}>{_('NonMemberControllers')}</label>
							<Table
								id={'holidayGroupDoesNotContainTable'}
								size='small'
								bordered
								columns={getColumns(true)}
								dataSource={controllersSource}
								rowSelection={getRowSelection(true)}
								loading={loadingSource}
								pagination={getPagination(true)}
								onChange={(pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<HolidayControllerColumn>) => {
									handleOnChangeTable(pagination, filters, sorter, true);
								}}
								scroll={tableScroll}
							/>
						</div>
						<div className={styles.middleContainer}>
							<Button
								id='holidayGroupAddController'
								type='primary'
								icon={<ArrowRightOutlined />}
								onClick={onAddControllerToHolidayGroup}
								disabled={disableAddButton}
							/>
							<Button
								id='holidayGroupRemoveController'
								type='primary'
								icon={<ArrowLeftOutlined />}
								onClick={onRemoveControllerFromHolidayGroup}
								disabled={disableRemoveButton}
							/>
						</div>
						<div className={styles.rightContainer}>
							<label className={styles.labelStyle}>{_('MemberControllers')}</label>
							<Table
								id={'holidayGroupContainTable'}
								size='small'
								bordered
								columns={getColumns(false)}
								dataSource={controllersTarget}
								rowSelection={getRowSelection(false)}
								loading={loadingTarget}
								pagination={getPagination(false)}
								onChange={(pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<HolidayControllerColumn>) => {
									handleOnChangeTable(pagination, filters, sorter, false);
								}}
								scroll={tableScroll}
							/>
						</div>
					</div>
				</div>
			</Spin>
		</Modal>
	);
};

export { HolidayGroupModal };
